import React, { useState } from "react";
import { format, isBefore, startOfDay } from "date-fns";
import { ChevronDown, Minus, Plus } from "lucide-react";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/** Adults + children must not exceed this (matches /book stay-row criteria). */
const MAX_GUESTS_TOTAL = 10;

export const BookingWidget = () => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isSelectingDeparture, setIsSelectingDeparture] = useState(false); // Track if we're waiting for departure

  const today = startOfDay(new Date());

  const maxAdultsGivenChildren = Math.max(1, MAX_GUESTS_TOTAL - children);
  const maxChildrenGivenAdults = Math.max(0, MAX_GUESTS_TOTAL - adults);

  // Alternating date selection: arrival → departure → new arrival → new departure
  const handleDateSelect = (range: DateRange | undefined) => {
    if (!range) {
      setDateRange(undefined);
      setIsSelectingDeparture(false);
      return;
    }

    // Case 1: Only 'from' is set (first click or new selection after complete range)
    if (range.from && !range.to) {
      // If we previously had both dates, this is a new arrival (third click, etc.)
      if (dateRange?.from && dateRange?.to) {
        setDateRange({
          from: range.from,
          to: undefined,
        });
        setIsSelectingDeparture(true);
        return;
      }
      
      // First click - set arrival only
      setDateRange({
        from: range.from,
        to: undefined,
      });
      setIsSelectingDeparture(true);
      return;
    }

    // Case 2: Both dates are set (second click - departure selected)
    if (range.from && range.to) {
      // Make sure departure is after arrival
      if (isBefore(range.to, range.from) || range.to.getTime() === range.from.getTime()) {
        // Invalid - treat as new arrival
        setDateRange({
          from: range.to,
          to: undefined,
        });
        setIsSelectingDeparture(true);
        return;
      }
      
      // Valid range
      setDateRange(range);
      setIsSelectingDeparture(false);
      return;
    }

    // Fallback
    setDateRange(range);
  };

  const handleBookNow = () => {
    // Construct URL with parameters
    const baseUrl = import.meta.env.VITE_BOOKING_URL;
    const checkInStr = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : "";
    const checkOutStr = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : "";
    
    // Check if URL already has params
    const separator = baseUrl.includes("?") ? "&" : "?";
    const finalUrl = `${baseUrl}${separator}checkIn=${checkInStr}&checkOut=${checkOutStr}&adults=${adults}&children=${children}`;
    
    window.location.assign(finalUrl);
  };

  // Determine helper text based on selection state
  const getHelperText = () => {
    if (!dateRange?.from) {
      return "Select your check-in date";
    }
    if (dateRange.from && !dateRange.to) {
      return "Select your check-out date";
    }
    // If both dates are selected, show message that next click will start new selection
    if (dateRange.from && dateRange.to) {
      return "Click any date to select new check-in";
    }
    return null;
  };

  return (
    <div className="relative z-30 mx-auto hidden w-full max-w-4xl px-4 sm:px-6 md:block">
      <div className="flex min-h-[3.75rem] items-stretch overflow-hidden rounded-full border border-gray-200 bg-white py-1 pl-1 pr-1 shadow-[0_8px_28px_rgba(0,0,0,0.1)] sm:min-h-[4rem] sm:pl-1.5 sm:pr-1.5">
        <div className="flex min-w-0 flex-1 items-stretch divide-x divide-gray-200">
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <div className="min-w-0 flex-[1.65]">
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="group flex h-full w-full min-w-0 cursor-pointer items-center gap-2 py-1 text-left transition-colors hover:bg-gray-50/90 sm:gap-3"
                >
                  <div className="flex min-h-[2.75rem] min-w-0 flex-1 divide-x divide-gray-200 sm:min-h-12">
                    <div className="flex w-1/2 min-w-0 flex-col justify-center gap-0.5 px-2.5 sm:px-3">
                      <span className="text-[9px] font-bold uppercase tracking-widest text-gray-500 font-lato group-hover:text-alpine-gold">
                        Arrival
                      </span>
                      <span className="truncate text-sm font-medium tabular-nums leading-tight text-neutral-900">
                        {dateRange?.from ? format(dateRange.from, "dd/MM/yyyy") : "Select date"}
                      </span>
                    </div>
                    <div className="flex w-1/2 min-w-0 flex-col justify-center gap-0.5 px-2.5 sm:px-3">
                      <span className="text-[9px] font-bold uppercase tracking-widest text-gray-500 font-lato group-hover:text-alpine-gold">
                        Departure
                      </span>
                      <span className="truncate text-sm font-medium tabular-nums leading-tight text-neutral-900">
                        {dateRange?.to ? format(dateRange.to, "dd/MM/yyyy") : "Select date"}
                      </span>
                    </div>
                  </div>
                  <ChevronDown className="mr-1 h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-pine-green sm:mr-2" aria-hidden />
                </button>
              </PopoverTrigger>
            </div>
              <PopoverContent 
                className="w-auto p-0" 
                align="start"
                sideOffset={8}
              >
                <div className="p-4">
                  {/* Helper text to guide user */}
                  {getHelperText() && (
                    <p className={cn(
                      "text-sm mb-3 text-center font-medium transition-colors",
                      dateRange?.from && !dateRange?.to 
                        ? "text-alpine-gold" 
                        : "text-gray-600"
                    )}>
                      {getHelperText()}
                    </p>
                  )}
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange?.from || new Date()}
                    selected={dateRange}
                    onSelect={handleDateSelect}
                    numberOfMonths={2}
                    disabled={(date) => isBefore(date, today)}
                    className="rounded-md"
                  />
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <div className="flex min-w-0 flex-1">
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="group flex h-full w-full min-w-0 cursor-pointer items-center gap-2 px-3 py-1 text-left transition-colors hover:bg-gray-50/90 sm:px-4"
                  >
                    <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
                      <span className="text-[9px] font-bold uppercase tracking-widest text-gray-500 font-lato group-hover:text-alpine-gold">
                        Guests
                      </span>
                      <span className="truncate text-sm font-medium leading-tight text-neutral-900">
                        {adults} {adults === 1 ? "Adult" : "Adults"}
                        {children > 0
                          ? `, ${children} ${children === 1 ? "Child" : "Children"}`
                          : ""}
                      </span>
                    </div>
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-pine-green" aria-hidden />
                  </button>
                </PopoverTrigger>
              </div>
              <PopoverContent className="w-72 p-4" align="center">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">Adults</div>
                      <div className="text-xs text-gray-500">Ages 13+</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => setAdults(Math.max(1, adults - 1))}
                        className="p-1 rounded-full hover:bg-gray-100 border border-gray-200"
                      >
                        <Minus className="w-4 h-4 text-gray-600" />
                      </button>
                      <span className="w-4 text-center font-medium">{adults}</span>
                      <button
                        type="button"
                        disabled={adults >= maxAdultsGivenChildren}
                        onClick={() =>
                          setAdults((a) => Math.min(maxAdultsGivenChildren, a + 1))
                        }
                        className="p-1 rounded-full border border-gray-200 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-30"
                      >
                        <Plus className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">Children</div>
                      <div className="text-xs text-gray-500">Ages 3 - 12</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => setChildren(Math.max(0, children - 1))}
                        className="p-1 rounded-full hover:bg-gray-100 border border-gray-200"
                      >
                        <Minus className="w-4 h-4 text-gray-600" />
                      </button>
                      <span className="w-4 text-center font-medium">{children}</span>
                      <button
                        type="button"
                        disabled={children >= maxChildrenGivenAdults}
                        onClick={() =>
                          setChildren((c) => Math.min(maxChildrenGivenAdults, c + 1))
                        }
                        className="p-1 rounded-full border border-gray-200 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-30"
                      >
                        <Plus className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
        </div>

        <div className="flex shrink-0 items-center self-stretch pl-1 sm:pl-1.5">
          <Button
            type="button"
            variant="hero"
            onClick={handleBookNow}
            trackingType="booking"
            trackingLabel="Check Availability"
            trackingLocation="Home hero booking bar"
            className={cn(
              "h-auto min-h-0 shrink-0 rounded-full border-0 px-4 py-1 shadow-md sm:px-5 sm:py-1.5",
              "flex flex-col items-center justify-center gap-0 leading-none",
              "text-xs hover:shadow-lg sm:text-[13px]",
            )}
          >
            <span className="font-semibold tracking-wide">Check Availability</span>
            <span className="mt-0.5 text-[7px] font-medium uppercase tracking-[0.12em] text-elegant-white/80 sm:text-[8px] sm:tracking-[0.14em]">
              Best Rate Guaranteed
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};
